import { getCentralUnits } from '@otovo/shared/cloudConfig/cloudConfig';
import { LanguageSwitcherPathResolver } from '@otovo/shared/components/Navigation/types';
import { productPageQuery } from '@otovo/shared/components/Sanity/ProductPages/sanityQueries';
import { StaticPropsContext } from '@otovo/shared/lib/next/types';
import { fetchSanityDocuments } from '@otovo/shared/lib/sanity/sanityApi';
import { getSanityClient } from '@otovo/shared/lib/sanity/sanityClient';
import { staticPathParamsByMarket } from '@otovo/shared/lib/staticPathParamsUtil';
import { Cloud$Market } from '@otovo/shared/types/cloudEnums';
import {
  Otovo$Config,
  Otovo$Locale,
  Otovo$StorefrontConfig,
} from '@otovo/shared/types/otovoweb';
import {
  Sanity$DocumentType,
  Sanity$ProductPage,
} from '@otovo/shared/types/sanityTypes';
import ProductLandingPage from '../../components/ProductPage/ProductLandingPage';
import { allLandingPagesQuery } from '../../components/SanityLandingPage/sanityQueries';
import StorefrontPage from '../../components/StorefrontPage';
import { getStaticProps as getStaticMarketProps } from '../../config/getProps';
import { getLocalizedPathRoot } from '../../lib/getLocalizedPathRoot';
import { fetchSanityDocumentWrapper } from '../../lib/sanity/fetchSanityDocumentWrapper';

type Props = {
  config: Otovo$Config;
  sanityDocument: Sanity$ProductPage;
};

const ProductPage = ({ config, sanityDocument }: Props) => {
  return (
    <StorefrontPage
      config={config}
      sanityDocument={sanityDocument}
      languageSwitcherPathResolver={languageSwitcherPathResolver}
    >
      <ProductLandingPage page={sanityDocument} />
    </StorefrontPage>
  );
};

const PAGE_TYPE: Sanity$DocumentType = 'productPage';

export async function getStaticPaths() {
  const centralUnitsWithStorefront = getCentralUnits().filter(
    (bu) => bu.STOREFRONT_ENABLED,
  );

  const fetchFunction = (market: Cloud$Market) =>
    fetchSanityDocuments(
      allLandingPagesQuery(PAGE_TYPE, '', market),
      getSanityClient(),
    );

  const servicePagePathsForAllMarkets = await staticPathParamsByMarket({
    fetchFunction,
    supportedMarkets: centralUnitsWithStorefront,
  });

  const paths = servicePagePathsForAllMarkets.map((path) => {
    const { locale } = path;

    return {
      ...path,
      params: {
        ...path.params,
        products: getLocalizedPathRoot('productPage', locale),
      },
    };
  });

  return {
    paths,
    fallback: 'blocking',
  };
}
export async function getStaticProps(context: StaticPropsContext) {
  const requestedTranslatedRoute = context.params.products;
  const availableTranslatedRoute = getLocalizedPathRoot(
    'productPage',
    context.locale,
  );

  if (requestedTranslatedRoute !== availableTranslatedRoute) {
    return {
      notFound: true,
    };
  }

  return fetchSanityDocumentWrapper({
    context,
    sanityQuery: (config: Otovo$StorefrontConfig) =>
      productPageQuery(
        PAGE_TYPE,
        context.params.slug,
        config.BU_CONFIG,
        context.locale,
      ),
    getStaticMarketProps,
  });
}

const languageSwitcherPathResolver: LanguageSwitcherPathResolver = (
  locale: Otovo$Locale,
  slug: string,
) => {
  const localizedProductsRoot = getLocalizedPathRoot('productPage', locale);
  return `/${locale}/${localizedProductsRoot}/${slug}/`;
};

export default ProductPage;
